export type PageToken = number | 'ellipsis-start' | 'ellipsis-end';

/**
 * The page tokens a pager shows: first, last, the current page with two
 * neighbours on each side, ellipses between. Up to 7 pages shows every page.
 *
 *   total 7:              [1, 2, 3, 4, 5, 6, 7]
 *   total 30, current 3:  [1, 2, 3, 4, 5, …, 30]
 *   total 30, current 15: [1, …, 13, 14, 15, 16, 17, …, 30]
 *   total 30, current 28: [1, …, 26, 27, 28, 29, 30]
 */
export const getPageNumbers = (currentPage: number, totalPages: number): PageToken[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: PageToken[] = [1];
  const showEllipsisStart = currentPage > 4;
  const showEllipsisEnd = currentPage < totalPages - 3;

  if (!showEllipsisStart) {
    for (let i = 2; i <= Math.min(5, totalPages - 1); i++) pages.push(i);
    if (totalPages > 6) pages.push('ellipsis-end');
    if (totalPages > 1) pages.push(totalPages);
  } else if (!showEllipsisEnd) {
    pages.push('ellipsis-start');
    for (let i = totalPages - 4; i < totalPages; i++) pages.push(i);
    pages.push(totalPages);
  } else {
    pages.push('ellipsis-start');
    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    pages.push('ellipsis-end');
    pages.push(totalPages);
  }
  return pages;
};

/**
 * Where a requested page lands once the total is known. NaN, 0 and negatives
 * go to page 1; past the end goes to the last page; a result set with no pages
 * (zero results) is page 1.
 */
export const clampPage = (requested: number, totalPages: number): number => {
  if (!Number.isFinite(requested) || requested < 1) return 1;
  if (totalPages < 1) return 1;
  return Math.min(Math.floor(requested), totalPages);
};
